import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type AttendanceOutput,
  type ClockInInput,
  type UserOutput,
  type LeaveRequestOutput,
  type CreateLeaveInput,
  LEAVE_TYPES,
  LEAVE_TYPE_LABELS,
  LEAVE_STATUS_LABELS,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { downloadFromApi } from '../lib/download.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { fmtDate } from '../lib/date.js';
import { DateInput } from '../components/DateInput.js';
import { useUserPreference } from '../lib/user-preferences.js';

export function AttendancePage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [from, setFrom] = useUserPreference('attendance.from', new Date().toISOString().slice(0, 10));
  const [to, setTo] = useUserPreference('attendance.to', new Date().toISOString().slice(0, 10));
  const [selectedUser, setSelectedUser] = useUserPreference('attendance.userId', '');
  const [showLeaveForm, setShowLeaveForm] = useState(false);

  const { canDo } = useCapabilities();
  const canManage = canDo('attendance.view_all');
  const canApprove = canDo('leave.approve');
  const isStaff = currentUser?.role !== 'CLIENT';

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    enabled: canManage,
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const status = useQuery({
    queryKey: ['attendance', 'status'],
    queryFn: async () => {
      const res = await fetch('/v1/attendance/status', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { clockedIn: boolean; clockedOut: boolean; record: AttendanceOutput | null } };
      return body.data;
    },
  });

  const list = useQuery({
    queryKey: ['attendance', { from, to, userId: selectedUser }],
    queryFn: async () => {
      const p = new URLSearchParams();
      p.set('from', new Date(from + 'T00:00:00.000Z').toISOString());
      p.set('to', new Date(to + 'T23:59:59.999Z').toISOString());
      if (selectedUser) p.set('userId', selectedUser);
      p.set('limit', '100');
      const res = await fetch(`/v1/attendance?${p.toString()}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: AttendanceOutput[]; total: number } };
      return body.data;
    },
  });

  const clockIn = useMutation({
    mutationFn: async (input: ClockInInput) => {
      const res = await fetch('/v1/attendance/clock-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attendance'] }),
  });

  const clockOut = useMutation({
    mutationFn: async () => {
      const res = await fetch('/v1/attendance/clock-out', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attendance'] }),
  });

  const onToggle = () => {
    if (!status.data?.clockedIn) {
      clockIn.mutate({});
    } else if (!status.data?.clockedOut) {
      clockOut.mutate();
    }
  };

  const buttonLabel = !status.data?.clockedIn
    ? (clockIn.isPending ? 'Clocking in…' : 'Clock in')
    : !status.data?.clockedOut
    ? (clockOut.isPending ? 'Clocking out…' : 'Clock out')
    : 'Done for today';

  const buttonClass = !status.data?.clockedIn
    ? 'bg-accent hover:bg-emerald-400 text-accent-fg'
    : !status.data?.clockedOut
    ? 'bg-amber-500 hover:bg-amber-400 text-oncolor'
    : 'bg-bg-card text-muted cursor-default';

  const userMap = new Map((users.data ?? []).map((u) => [u.id, u]));

  function exportAttendance() {
    const p = new URLSearchParams();
    p.set('from', new Date(from + 'T00:00:00.000Z').toISOString());
    p.set('to', new Date(to + 'T23:59:59.999Z').toISOString());
    if (selectedUser) p.set('userId', selectedUser);
    downloadFromApi(`/v1/attendance/export?${p.toString()}`, 'attendance.csv')
      .catch((e) => alert(e instanceof Error ? e.message : 'Export failed'));
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Attendance</h1>
          <p className="text-sm text-muted">{list.data?.total ?? '—'} records</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportAttendance} className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg">Export</button>
          {isStaff && canDo('leave.request') && (
            <button onClick={() => setShowLeaveForm(true)} className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg">Request leave</button>
          )}
          {isStaff && (
            <button
              onClick={onToggle}
              disabled={status.data?.clockedOut === true || clockIn.isPending || clockOut.isPending}
              className={`text-sm font-medium px-3 py-1.5 rounded-lg disabled:opacity-50 ${buttonClass}`}
            >
              {buttonLabel}
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted">From</label>
          <DateInput value={from} onChange={(e) => setFrom(e.target.value)} className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted">To</label>
          <DateInput value={to} onChange={(e) => setTo(e.target.value)} className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white" />
        </div>
        {canManage && (
          <SearchableSelect
            value={selectedUser}
            onChange={setSelectedUser}
            placeholder="All staff"
            options={(users.data ?? []).filter((u) => u.status !== 'DISABLED').map((u) => ({ value: u.id, label: u.displayName }))}
            className="w-full bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-accent"
            wrapperClassName="w-full sm:w-52"
          />
        )}
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Clock in</th>
              <th className="px-4 py-3 font-medium">Clock out</th>
              <th className="px-4 py-3 font-medium">Hours</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={5} />}
            {list.error && <tr><td colSpan={5} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>}
            {list.data?.items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">No records.</td></tr>}
            {list.data?.items.map((a) => (
              <tr key={a.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3 text-white">{userMap.get(a.userId)?.displayName ?? '—'}</td>
                <td className="px-4 py-3 text-white">{fmtDate(a.date)}</td>
                <td className="px-4 py-3 text-muted">{a.clockInAt ? fmtTime(a.clockInAt) : '—'}</td>
                <td className="px-4 py-3 text-muted">{a.clockOutAt ? fmtTime(a.clockOutAt) : '—'}</td>
                <td className="px-4 py-3 text-muted">{hours(a.clockInAt, a.clockOutAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isStaff && <LeaveSection canManage={canApprove} />}

      {showLeaveForm && <RequestLeaveModal onClose={() => setShowLeaveForm(false)} />}
    </div>
  );
}

const LEAVE_STATUS_PILL: Record<string, string> = {
  PENDING: 'bg-amber-500/15 text-amber-300',
  APPROVED: 'bg-emerald-500/15 text-emerald-300',
  REJECTED: 'bg-rose-500/15 text-rose-300',
  CANCELLED: 'bg-bg-hover text-muted',
};

function leaveRange(r: LeaveRequestOutput) {
  return r.fromDate === r.toDate ? fmtDate(r.fromDate) : `${fmtDate(r.fromDate)} → ${fmtDate(r.toDate)}`;
}

function LeaveSection({ canManage }: { canManage: boolean }) {
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<LeaveRequestOutput | null>(null);
  const [queueStatus, setQueueStatus] = useState<'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'ALL'>('PENDING');

  const mine = useQuery({
    queryKey: ['leave', 'mine'],
    queryFn: async () => {
      const res = await fetch('/v1/leave?mine=1', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: LeaveRequestOutput[] } };
      return body.data.items;
    },
    // Pending on top, then newest first — decided requests stay visible below.
    select: (items) => [...items].sort((a, b) =>
      (a.status === 'PENDING' ? 0 : 1) - (b.status === 'PENDING' ? 0 : 1) || b.createdAt.localeCompare(a.createdAt)),
  });

  const queue = useQuery({
    queryKey: ['leave', 'queue', queueStatus],
    enabled: canManage,
    queryFn: async () => {
      const url = queueStatus === 'ALL' ? '/v1/leave' : `/v1/leave?status=${queueStatus}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: LeaveRequestOutput[] } };
      return body.data.items;
    },
  });

  const act = useMutation({
    mutationFn: async ({ id, action, note }: { id: string; action: 'approve' | 'reject' | 'cancel'; note?: string }) => {
      const res = await fetch(`/v1/leave/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(note ? { note } : {}),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      setActionError(null);
      setRejecting(null);
      queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
    onError: (err) => setActionError((err as Error).message),
  });

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-white">Leave</h2>
      {actionError && <div className="text-sm text-rose-300">{actionError}</div>}

      {canManage && (
        <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
          <div className="px-4 py-3 bg-bg-card border-b border-border text-xs text-muted font-medium flex items-center justify-between gap-3">
            <span>{queueStatus === 'PENDING' ? 'Approvals queue' : 'Leave decisions'}</span>
            <select
              value={queueStatus}
              onChange={(e) => setQueueStatus(e.target.value as typeof queueStatus)}
              className="bg-bg-panel border border-border rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-accent"
            >
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="ALL">All</option>
            </select>
          </div>
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Dates</th>
                <th className="px-4 py-2 font-medium">Reason</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {queue.isLoading && <TableSkeletonRows colSpan={6} />}
              {queue.error && <tr><td colSpan={6} className="px-4 py-8 text-center text-rose-400">{(queue.error as Error).message}</td></tr>}
              {queue.data?.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted">{queueStatus === 'PENDING' ? 'No pending requests.' : 'No requests with this status.'}</td></tr>}
              {queue.data?.map((r) => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                  <td className="px-4 py-3 text-white">{r.userName ?? '—'}</td>
                  <td className="px-4 py-3 text-muted">{LEAVE_TYPE_LABELS[r.type]}</td>
                  <td className="px-4 py-3 text-white">{leaveRange(r)}</td>
                  <td className="px-4 py-3 text-muted break-words max-w-[240px]">{r.reason ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${LEAVE_STATUS_PILL[r.status] ?? 'bg-bg-hover text-muted'}`}>
                      {LEAVE_STATUS_LABELS[r.status]}
                    </span>
                    {r.decisionNote && <span className="text-xs text-muted ml-2">{r.decisionNote}</span>}
                    {r.decidedByName && <div className="text-[11px] text-muted mt-0.5">by {r.decidedByName}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => act.mutate({ id: r.id, action: 'approve' })}
                          disabled={act.isPending}
                          className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-xs font-medium px-2.5 py-1 rounded-lg mr-2"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => setRejecting(r)}
                          disabled={act.isPending}
                          className="border btn-ghost-danger text-xs px-2.5 py-1 rounded-lg"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <div className="px-4 py-3 bg-bg-card border-b border-border text-xs text-muted font-medium">My leave</div>
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="text-left text-xs text-muted border-b border-border">
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium">Dates</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Decided by</th>
              <th className="px-4 py-2 font-medium text-right"></th>
            </tr>
          </thead>
          <tbody>
            {mine.isLoading && <TableSkeletonRows colSpan={5} />}
            {mine.error && <tr><td colSpan={5} className="px-4 py-8 text-center text-rose-400">{(mine.error as Error).message}</td></tr>}
            {mine.data?.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">No leave requests yet.</td></tr>}
            {mine.data?.map((r) => (
              <tr key={r.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3 text-white">{LEAVE_TYPE_LABELS[r.type]}</td>
                <td className="px-4 py-3 text-white">{leaveRange(r)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${LEAVE_STATUS_PILL[r.status] ?? 'bg-bg-hover text-muted'}`}>
                    {LEAVE_STATUS_LABELS[r.status]}
                  </span>
                  {r.decisionNote && <span className="text-xs text-muted ml-2">{r.decisionNote}</span>}
                </td>
                <td className="px-4 py-3 text-muted">{r.decidedByName ?? '—'}</td>
                <td className="px-4 py-3 text-right">
                  {r.status === 'PENDING' && (
                    <button
                      onClick={() => act.mutate({ id: r.id, action: 'cancel' })}
                      disabled={act.isPending}
                      className="text-xs text-muted hover:text-white border border-border px-2.5 py-1 rounded-lg disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rejecting && (
        <RejectLeaveModal
          request={rejecting}
          submitting={act.isPending}
          error={actionError}
          onClose={() => setRejecting(null)}
          onConfirm={(note) => act.mutate({ id: rejecting.id, action: 'reject', note })}
        />
      )}
    </div>
  );
}

function RequestLeaveModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<CreateLeaveInput['type']>('ANNUAL');
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/v1/leave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ type, fromDate, toDate, ...(reason.trim() ? { reason: reason.trim() } : {}) }),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl animate-pop p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Request leave</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-3">
          <div>
            <label className="block text-xs text-muted mb-1">Type <span className="text-rose-400">*</span></label>
            <select value={type} onChange={(e) => setType(e.target.value as CreateLeaveInput['type'])} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">
              {LEAVE_TYPES.map((t) => <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">From <span className="text-rose-400">*</span></label>
              <DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">To <span className="text-rose-400">*</span></label>
              <DateInput value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={3} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" />
          </div>
          {mutation.error && <p className="text-xs text-rose-400">{(mutation.error as Error).message}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button type="submit" disabled={mutation.isPending} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
              {mutation.isPending ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RejectLeaveModal({ request, submitting, error, onClose, onConfirm }: {
  request: LeaveRequestOutput;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (note?: string) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl animate-pop p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Reject leave</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <p className="text-sm text-muted mb-3">
          Reject {request.userName ?? 'this request'}'s {LEAVE_TYPE_LABELS[request.type].toLowerCase()} leave ({leaveRange(request)})?
        </p>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} placeholder="Note (optional)" className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white mb-3" />
        {error && <p className="text-xs text-rose-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            onClick={() => onConfirm(note.trim() || undefined)}
            disabled={submitting}
            className="bg-rose-700 hover:bg-rose-600 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {submitting ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}


function fmtTime(d: string) {
  return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function hours(inAt: string | null, outAt: string | null) {
  if (!inAt || !outAt) return '—';
  const ms = new Date(outAt).getTime() - new Date(inAt).getTime();
  const h = ms / 3600000;
  return `${h.toFixed(2)}h`;
}
